import { useState, useEffect } from 'react';
import { X, FileText, Loader2, CheckCircle2, XCircle, AlertCircle, User } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { ScrollArea } from './ui/scroll-area';
import { useNotificationStore } from '@/stores';
import { useJobPollingStore } from '@/stores/jobPollingStore';
import { documentApi, userApi } from '@/services/api';
import { addLoadingDocument, removeLoadingDocument } from '@/utils/loadingDocuments';

interface BatchDocumentOnboardingProps {
  onClose: () => void;
  contextData?: any;
  onDocumentAdded?: () => void;
  onSwitchToSingle?: () => void;
}

type DocumentStatus = 'pending' | 'processing' | 'success' | 'error';

interface DocumentItem {
  id: string;
  cleanedId: string;
  status: DocumentStatus;
  error?: string;
  jobId?: string;
}

const RESOURCE_TYPES = [
  { id: 'patent', label: 'US Patent', description: 'Public US Patent Numbers' },
  { id: 'publication', label: 'US Publication', description: 'Public US Publication Numbers' },
  { id: 'application', label: 'US Application', description: 'Public US Application Numbers' },
  { id: 'foreign_patent', label: 'Foreign Patent', description: 'Foreign Patent Numbers' },
];

const BATCH_SIZE = 3;

export function BatchDocumentOnboarding({
  onClose,
  contextData,
  onDocumentAdded,
  onSwitchToSingle,
}: BatchDocumentOnboardingProps) {
  const [selectedType, setSelectedType] = useState<string>('');
  const [documentIds, setDocumentIds] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const { addNotification } = useNotificationStore();
  const { startPolling } = useJobPollingStore();
  const [usageData, setUsageData] = useState<any>(null);
  const [loadingUsage, setLoadingUsage] = useState(true);

  // Fetch usage data on mount
  useEffect(() => {
    const fetchUsageData = async () => {
      try {
        const response = await userApi.fetchUsage();
        if (response) {
          setUsageData(response);
        }
      } catch (error) {
        console.error('Failed to fetch usage data:', error);
      } finally {
        setLoadingUsage(false);
      }
    };

    fetchUsageData();
  }, []);

  // Listen for job completion events
  useEffect(() => {
    const handleJobComplete = (event: CustomEvent) => {
      const { jobId, docType, docId, success, error } = event.detail;
      console.log('[BatchDocumentOnboarding] Job completion event received:', event.detail);

      setDocuments(prev => prev.map(doc => {
        if (doc.jobId === jobId) {
          if (success) {
            return { ...doc, status: 'success' as DocumentStatus };
          } else {
            return { ...doc, status: 'error' as DocumentStatus, error: error || 'Failed to process' };
          }
        }
        return doc;
      }));

      // Format type name properly
      const resourceTypeName = docType === 'foreign_patent'
        ? 'Foreign Patent'
        : docType.charAt(0).toUpperCase() + docType.slice(1);

      if (success) {
        removeLoadingDocument(docId, resourceTypeName);
        if (onDocumentAdded) {
          onDocumentAdded();
        }
      } else {
        removeLoadingDocument(docId, resourceTypeName);

        // Remove from context on error
        const stored = localStorage.getItem('session_context');
        const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};

        if (docType === 'patent') {
          context.current_patents = (context.current_patents || []).filter((id: string) => id !== docId);
        } else if (docType === 'publication') {
          context.current_publications = (context.current_publications || []).filter((id: string) => id !== docId);
        } else if (docType === 'application') {
          context.current_applications = (context.current_applications || []).filter((id: string) => id !== docId);
        } else if (docType === 'foreign_patent') {
          context.current_foreign_patents = (context.current_foreign_patents || []).filter((id: string) => id !== docId);
        }

        localStorage.setItem('session_context', JSON.stringify(context));
        window.dispatchEvent(new Event('storage-update'));
      }
    };

    window.addEventListener('job-complete', handleJobComplete as EventListener);

    return () => {
      window.removeEventListener('job-complete', handleJobComplete as EventListener);
    };
  }, [onDocumentAdded]);

  const validateNumbers = (input: string, resourceType: string) => {
    if (!input.trim()) {
      setValidationErrors([]);
      return;
    }

    const entries = input
      .split(/[\s\n,]+/)
      .map((entry) => entry.trim())
      .filter(Boolean);

    const errors: string[] = [];

    entries.forEach((entry, index) => {
      if (resourceType === 'foreign_patent') {
        const pattern = /^[A-Z]{2,3}\d{5,10}[A-Z]\d*$/i;
        if (!pattern.test(entry.replace(/\s/g, ''))) {
          errors.push(
            `Entry ${index + 1} "${entry}" does not match foreign patent format (e.g., EP1234567A1, WO2024123456A1)`
          );
        }
      } else {
        let cleaned = entry
          .replace(/,/g, '')
          .replace(/\//g, '')
          .replace(/^US/i, '')
          .replace(/[A-Z]\d*$/i, '');

        if (resourceType === 'publication') {
          const pattern = /^2\d{3}0?\d{6}$/;
          if (!pattern.test(cleaned)) {
            errors.push(
              `Entry ${index + 1} "${entry}" does not match publication format (2DDDNNNNNN)`
            );
          }
        } else if (resourceType === 'patent') {
          const pattern = /^\d{7,8}$/;
          if (!pattern.test(cleaned)) {
            errors.push(
              `Entry ${index + 1} "${entry}" does not match patent format (7 or 8 digits)`
            );
          }
        } else if (resourceType === 'application') {
          const pattern = /^\d{8}$/;
          if (!pattern.test(cleaned)) {
            errors.push(
              `Entry ${index + 1} "${entry}" does not match application format (8 digits)`
            );
          }
        }
      }
    });

    setValidationErrors(errors);
  };

  const cleanDocumentId = (id: string, resourceType: string): string => {
    let cleaned = id.replace(/,/g, '').replace(/\//g, '').replace(/\s/g, '');

    if (resourceType === 'foreign_patent') {
      cleaned = cleaned.toUpperCase();
    } else if (['publication', 'patent', 'application'].includes(resourceType)) {
      cleaned = cleaned.replace(/^US/i, '').replace(/[A-Z]\d*$/i, '');

      // For publications, ensure format is 2DDD0NNNNNN
      if (
        resourceType === 'publication' &&
        cleaned.length === 10 &&
        /^2\d{3}\d{6}$/.test(cleaned)
      ) {
        cleaned = cleaned.slice(0, 4) + '0' + cleaned.slice(4);
      }
    }

    return cleaned;
  };

  const handleStartBatch = () => {
    if (!selectedType) {
      addNotification({
        type: 'error',
        title: 'No Resource Type Selected',
        message: 'Please select a resource type first',
      });
      return;
    }

    const entries = documentIds
      .split(/[\s\n,]+/)
      .map((id) => id.trim())
      .filter(Boolean);

    if (entries.length === 0) {
      addNotification({
        type: 'error',
        title: 'No Documents Entered',
        message: `Please enter at least one ${selectedType} number`,
      });
      return;
    }

    if (validationErrors.length > 0) {
      addNotification({
        type: 'error',
        title: 'Validation Errors',
        message: 'Please fix the validation errors before submitting',
      });
      return;
    }

    // Check daily quota before processing batch
    if (usageData?.daily_onboarding) {
      const remaining = usageData.daily_onboarding.patent_publications.remaining;
      if (remaining <= 0) {
        addNotification({
          type: 'error',
          title: 'Daily Limit Reached',
          message: `You have reached your daily limit for patent/publication onboarding (${usageData.daily_onboarding.patent_publications.limit} per day). The limit will reset tomorrow.`,
          duration: 8000,
        });
        return;
      }
      if (entries.length > remaining) {
        addNotification({
          type: 'warning',
          title: 'Exceeds Daily Limit',
          message: `You are trying to add ${entries.length} documents but only have ${remaining} remaining today. The batch will stop once the limit is reached.`,
          duration: 8000,
        });
        // Still allow processing, but it will stop when limit is reached
      }
    }

    // Prepare documents
    const preparedDocs: DocumentItem[] = entries.map((id) => ({
      id,
      cleanedId: cleanDocumentId(id, selectedType),
      status: 'pending' as DocumentStatus,
    }));

    setDocuments(preparedDocs);
    setTotalBatches(Math.ceil(preparedDocs.length / BATCH_SIZE));
    setCurrentBatch(0);
    setIsProcessing(true);
    setShowResults(true);

    // Start processing batches
    processBatches(preparedDocs, selectedType);
  };

  const processBatches = async (docs: DocumentItem[], resourceType: string) => {
    const batches: DocumentItem[][] = [];
    for (let i = 0; i < docs.length; i += BATCH_SIZE) {
      batches.push(docs.slice(i, i + BATCH_SIZE));
    }

    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      setCurrentBatch(batchIndex + 1);
      const batch = batches[batchIndex];

      // Process all documents in the current batch in parallel
      await Promise.all(
        batch.map(async (doc) => {
          await processDocument(doc, resourceType);
        })
      );
    }

    setIsProcessing(false);

    // Show completion notification
    const successCount = docs.filter(d => d.status === 'success').length;
    const errorCount = docs.filter(d => d.status === 'error').length;

    addNotification({
      type: successCount === docs.length ? 'success' : 'warning',
      title: 'Batch Processing Complete',
      message: `Successfully processed ${successCount} of ${docs.length} documents${errorCount > 0 ? ` (${errorCount} failed)` : ''}`,
      duration: 5000,
    });
  };

  const processDocument = async (doc: DocumentItem, resourceType: string) => {
    try {
      // Update status to processing
      setDocuments(prev => prev.map(d =>
        d.cleanedId === doc.cleanedId ? { ...d, status: 'processing' as DocumentStatus } : d
      ));

      // Add to session context immediately
      const stored = localStorage.getItem('session_context');
      const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};

      if (resourceType === 'patent') {
        context.current_patents = [...(context.current_patents || []), doc.cleanedId];
      } else if (resourceType === 'publication') {
        context.current_publications = [...(context.current_publications || []), doc.cleanedId];
      } else if (resourceType === 'application') {
        context.current_applications = [...(context.current_applications || []), doc.cleanedId];
      } else if (resourceType === 'foreign_patent') {
        context.current_foreign_patents = [...(context.current_foreign_patents || []), doc.cleanedId];
      }

      localStorage.setItem('session_context', JSON.stringify(context));
      window.dispatchEvent(new Event('storage-update'));

      // Call API to fetch and load document
      const response = await documentApi.fetchAndLoadDocument(resourceType, doc.cleanedId);

      // Check if response is an error (backend returns errors with 200 status)
      const responseData = Array.isArray(response) ? response[0] : response;

      if (responseData?.error === 'daily_limit_exceeded') {
        // Mark document as failed with quota error
        setDocuments(prev => prev.map(d =>
          d.cleanedId === doc.cleanedId
            ? { ...d, status: 'error' as const, error: responseData.message }
            : d
        ));

        // Show notification with longer duration and add to notification center
        addNotification({
          type: 'error',
          title: 'Daily Limit Reached',
          message: `${responseData.message} Remaining documents will not be processed. The limit will reset tomorrow.`,
          duration: 20000,
          persistent: true,
        });

        // Refresh usage data
        try {
          const usageResponse = await userApi.fetchUsage();
          if (usageResponse) {
            setUsageData(usageResponse);
          }
        } catch (e) {
          console.error('Failed to refresh usage data:', e);
        }

        return; // Stop processing this document
      }

      if (response?.job_id) {
        // Update document with job ID
        setDocuments(prev => prev.map(d =>
          d.cleanedId === doc.cleanedId ? { ...d, jobId: response.job_id } : d
        ));

        // Add to loading documents tracking
        const resourceTypeName = resourceType === 'foreign_patent'
          ? 'Foreign Patent'
          : resourceType.charAt(0).toUpperCase() + resourceType.slice(1);

        addLoadingDocument(doc.cleanedId, resourceTypeName, doc.cleanedId);

        // Start polling for job status
        startPolling(response.job_id, resourceType, doc.cleanedId);
      } else {
        throw new Error('No job ID returned');
      }
    } catch (error: any) {
      console.error(`Failed to process ${resourceType} ${doc.cleanedId}:`, error);

      // Extract error data - Flask returns [data, status_code] as array
      const responseData = Array.isArray(error?.response?.data)
        ? error.response.data[0]
        : error?.response?.data;

      // Check if it's a quota exceeded error
      let errorMessage = 'Failed to submit';
      if (responseData?.error === 'daily_limit_exceeded') {
        errorMessage = 'Daily limit reached';
        // Refresh usage data
        try {
          const response = await userApi.fetchUsage();
          if (response) {
            setUsageData(response);
          }
        } catch (e) {
          console.error('Failed to refresh usage data:', e);
        }
      }

      // Update status to error
      setDocuments(prev => prev.map(d =>
        d.cleanedId === doc.cleanedId
          ? { ...d, status: 'error' as DocumentStatus, error: errorMessage }
          : d
      ));

      // Remove from context on error
      const stored = localStorage.getItem('session_context');
      const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};

      if (resourceType === 'patent') {
        context.current_patents = (context.current_patents || []).filter((id: string) => id !== doc.cleanedId);
      } else if (resourceType === 'publication') {
        context.current_publications = (context.current_publications || []).filter((id: string) => id !== doc.cleanedId);
      } else if (resourceType === 'application') {
        context.current_applications = (context.current_applications || []).filter((id: string) => id !== doc.cleanedId);
      } else if (resourceType === 'foreign_patent') {
        context.current_foreign_patents = (context.current_foreign_patents || []).filter((id: string) => id !== doc.cleanedId);
      }

      localStorage.setItem('session_context', JSON.stringify(context));
      window.dispatchEvent(new Event('storage-update'));
    }
  };

  const getStatusIcon = (status: DocumentStatus) => {
    switch (status) {
      case 'pending':
        return <div className="w-5 h-5 rounded-full border-2 border-gray-300" />;
      case 'processing':
        return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'success':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
    }
  };

  const getStatusColor = (status: DocumentStatus) => {
    switch (status) {
      case 'pending':
        return 'border-gray-200 bg-white';
      case 'processing':
        return 'border-blue-200 bg-blue-50';
      case 'success':
        return 'border-green-200 bg-green-50';
      case 'error':
        return 'border-red-200 bg-red-50';
    }
  };

  const successCount = documents.filter(d => d.status === 'success').length;
  const errorCount = documents.filter(d => d.status === 'error').length;
  const processingCount = documents.filter(d => d.status === 'processing').length;
  const pendingCount = documents.filter(d => d.status === 'pending').length;
  const progress = documents.length > 0 ? ((successCount + errorCount) / documents.length) * 100 : 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Batch Document Onboarding
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Add multiple documents at once - processes 3 at a time
            </p>
            {!loadingUsage && usageData?.daily_onboarding && selectedType && (
              <div className="mt-2 flex items-center gap-2">
                <Badge
                  variant={usageData.daily_onboarding.patent_publications.remaining <= 0 ? 'destructive' : usageData.daily_onboarding.patent_publications.remaining <= 5 ? 'secondary' : 'default'}
                  className="text-xs"
                >
                  {usageData.daily_onboarding.patent_publications.remaining} / {usageData.daily_onboarding.patent_publications.limit} remaining today
                </Badge>
                {usageData.daily_onboarding.patent_publications.remaining <= 5 && usageData.daily_onboarding.patent_publications.remaining > 0 && (
                  <span className="text-xs text-amber-600">Approaching daily limit</span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {onSwitchToSingle && !isProcessing && (
              <Button
                variant="outline"
                size="sm"
                onClick={onSwitchToSingle}
                className="gap-2"
              >
                <User className="w-4 h-4" />
                Single Mode
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={onClose} disabled={isProcessing}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {!showResults ? (
              <>
                {/* Resource Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-3">
                    Select Document Type
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {RESOURCE_TYPES.map((type) => {
                      return (
                        <button
                          key={type.id}
                          onClick={() => {
                            setSelectedType(type.id);
                            setDocumentIds('');
                            setValidationErrors([]);
                          }}
                          className={`flex items-start gap-3 p-4 rounded-lg border-2 transition-all text-left ${
                            selectedType === type.id
                              ? 'border-blue-600 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300 bg-white'
                          }`}
                        >
                          <FileText
                            className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                              selectedType === type.id
                                ? 'text-blue-600'
                                : 'text-gray-400'
                            }`}
                          />
                          <div className="flex-1 min-w-0">
                            <div
                              className={`font-medium text-sm ${
                                selectedType === type.id
                                  ? 'text-blue-900'
                                  : 'text-gray-900'
                              }`}
                            >
                              {type.label}
                            </div>
                            <div className="text-xs text-gray-600 mt-0.5">
                              {type.description}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Document Entry */}
                {selectedType && (
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Enter Document Numbers (one per line or comma-separated)
                    </label>
                    <Textarea
                      rows={8}
                      value={documentIds}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setDocumentIds(newValue);
                        validateNumbers(newValue, selectedType);
                      }}
                      className="resize-none font-mono text-sm"
                      placeholder={
                        selectedType === 'publication'
                          ? '20240123456, US20240123457A1, 2024/0123458\n20240234567\n20240345678'
                          : selectedType === 'patent'
                          ? '12345678, US12,345,679, 12/345,680\n12345681\n12345682'
                          : selectedType === 'application'
                          ? '12345678, 12/345,679\n12345680\n12345681'
                          : selectedType === 'foreign_patent'
                          ? 'EP1234567A1, WO2024123456A1, CN112345678A\nEP2345678B1\nWO2024234567A2'
                          : ''
                      }
                    />

                    {/* Validation Errors */}
                    {validationErrors.length > 0 && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="font-medium text-sm text-red-900 mb-2">
                          Validation Errors:
                        </div>
                        <ScrollArea className="max-h-32">
                          <ul className="list-disc list-inside space-y-1 text-xs text-red-700">
                            {validationErrors.map((error, idx) => (
                              <li key={idx}>{error}</li>
                            ))}
                          </ul>
                        </ScrollArea>
                      </div>
                    )}

                    {/* Format Help */}
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-blue-900">
                          {selectedType === 'publication' &&
                            'Expected format: 2DDDNNNNNN (e.g., 20240123456). Separate multiple entries with commas or new lines.'}
                          {selectedType === 'patent' &&
                            'Expected format: 7 or 8 digits (e.g., 9424461, 12345678). Separate multiple entries with commas or new lines.'}
                          {selectedType === 'application' &&
                            'Expected format: 8 digits (e.g., 12345678). Separate multiple entries with commas or new lines.'}
                          {selectedType === 'foreign_patent' &&
                            'Expected format: CC#######K (e.g., EP1234567A1, WO2024123456A1). Separate multiple entries with commas or new lines.'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Progress Summary */}
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {isProcessing ? `Processing Batch ${currentBatch} of ${totalBatches}` : 'Batch Processing Complete'}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          {successCount} success, {errorCount} failed, {processingCount} processing, {pendingCount} pending
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-blue-600">
                          {Math.round(progress)}%
                        </div>
                        <div className="text-xs text-gray-600">complete</div>
                      </div>
                    </div>
                    <Progress value={progress} className="h-2" />
                  </div>

                  {/* Status Summary */}
                  <div className="grid grid-cols-4 gap-3">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-green-700">{successCount}</div>
                      <div className="text-xs text-green-600 mt-1">Success</div>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-red-700">{errorCount}</div>
                      <div className="text-xs text-red-600 mt-1">Failed</div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-blue-700">{processingCount}</div>
                      <div className="text-xs text-blue-600 mt-1">Processing</div>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-gray-700">{pendingCount}</div>
                      <div className="text-xs text-gray-600 mt-1">Pending</div>
                    </div>
                  </div>

                  {/* Document List */}
                  <div>
                    <div className="text-sm font-medium text-gray-900 mb-3">
                      Documents ({documents.length} total)
                    </div>
                    <ScrollArea className="h-64 border border-gray-200 rounded-lg">
                      <div className="p-2 space-y-2">
                        {documents.map((doc, index) => (
                          <div
                            key={index}
                            className={`p-3 rounded-lg border transition-all ${getStatusColor(doc.status)}`}
                          >
                            <div className="flex items-center gap-3">
                              {getStatusIcon(doc.status)}
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm text-gray-900">
                                  {doc.cleanedId}
                                </div>
                                {doc.error && (
                                  <div className="text-xs text-red-600 mt-1">
                                    Error: {doc.error}
                                  </div>
                                )}
                              </div>
                              <Badge
                                variant={
                                  doc.status === 'success' ? 'default' :
                                  doc.status === 'error' ? 'destructive' :
                                  doc.status === 'processing' ? 'secondary' :
                                  'outline'
                                }
                                className="text-xs"
                              >
                                {doc.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          {!showResults ? (
            <>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={handleStartBatch}
                disabled={!selectedType || !documentIds.trim() || validationErrors.length > 0}
              >
                Start Batch Processing
              </Button>
            </>
          ) : (
            <>
              {!isProcessing && (
                <Button onClick={onClose} className="gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Done
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
